import { useState, useCallback, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Stepper,
  Step,
  StepLabel,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Checkbox,
  InputAdornment,
  IconButton,
  Alert,
  LinearProgress,
  Chip,
  Divider,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Check as CheckIcon,
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  Celebration as CelebrationIcon,
  CalendarMonth as CalendarIcon,
} from '@mui/icons-material';
import { useThemeCustom } from '../../hooks/useThemeCustom';
import professionalsService from '../../services/professionalsService';
import servicesService from '../../services/servicesService';
import placesService from '../../services/placesService';
import placeProfessionalsService from '../../services/placeProfessionalsService';
import placeServicesService from '../../services/placeServicesService';
import placeServiceProfessionalsService from '../../services/placeServiceProfessionalsService';
import ScheduleCreationWizard from '../schedules/ScheduleCreationWizard';

/**
 * PlaceCreationWizard - Wizard multi-step para criação de locais
 * 
 * Steps:
 * 1. Nome do local
 * 2. Selecionar profissionais
 * 3. Serviços por profissional (repetido)
 * 4. Resumo
 * 5. Criação (progresso)
 */

// Função helper para delay entre chamadas de API
const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));
const API_DELAY_MS = 1000; // 1 segundo entre cada chamada

export default function PlaceCreationWizard({ 
  open, 
  onClose, 
  onSuccess,
  existingProfessionals = [],
  existingServices = [],
}) {
  // Theme context para labels personalizados
  const { getLabel } = useThemeCustom();
  const placeLabel = getLabel('places')?.toLowerCase() || 'local';
  const placeSingular = getLabel('placesSingular')?.toLowerCase() || 'local';
  const placePreposition = getLabel('placesPreposition') || 'do';
  const placeArticle = getLabel('placesArticle') || 'o';
  
  // Estado do wizard
  const [activeStep, setActiveStep] = useState(0);
  const [placeName, setPlaceName] = useState('');
  
  // Profissionais selecionados/novos
  // { id: string | null, name: string, selected: boolean }
  const [professionals, setProfessionals] = useState([]);
  const [newProfessionalName, setNewProfessionalName] = useState('');
  
  // Serviços disponíveis (inclui novos criados durante o wizard)
  const [allServices, setAllServices] = useState([]);
  
  // Serviços selecionados por índice de profissional
  // { [profIndex]: Set<serviceKey> } onde serviceKey = id ou 'new_name'
  const [professionalServices, setProfessionalServices] = useState({});
  const [newServiceName, setNewServiceName] = useState('');
  
  // Índice do profissional atual no step 3
  const [currentProfessionalIndex, setCurrentProfessionalIndex] = useState(0);
  
  // Estado de criação (step 5)
  const [creating, setCreating] = useState(false);
  const [creationProgress, setCreationProgress] = useState(0);
  const [creationStatus, setCreationStatus] = useState([]);
  const [creationError, setCreationError] = useState(null);
  const [creationComplete, setCreationComplete] = useState(false);
  
  // Estado para dados do local criado (para usar no wizard de agenda)
  const [createdPlaceData, setCreatedPlaceData] = useState(null);
  
  // Estado para wizard de criação de agenda
  const [showScheduleWizard, setShowScheduleWizard] = useState(false);

  // Inicializar dados quando o dialog abre
  const handleDialogEnter = useCallback(() => {
    console.log('[PlaceCreationWizard] 🚀 Dialog aberto - Inicializando wizard');
    console.log('[PlaceCreationWizard] 📋 Profissionais existentes recebidos:', existingProfessionals);
    console.log('[PlaceCreationWizard] 📋 Serviços existentes recebidos:', existingServices);
    
    // Reset estado
    setActiveStep(0);
    setPlaceName('');
    setNewProfessionalName('');
    setNewServiceName('');
    setCreatedPlaceData(null);
    setShowScheduleWizard(false);
    setCurrentProfessionalIndex(0);
    setCreating(false);
    setCreationProgress(0);
    setCreationStatus([]);
    setCreationError(null);
    setCreationComplete(false);
    
    // Inicializar profissionais existentes
    const profs = existingProfessionals.map(p => ({
      id: p.id,
      name: p.name,
      selected: false,
    }));
    setProfessionals(profs);
    setProfessionalServices({});
    console.log('[PlaceCreationWizard] ✅ Profissionais inicializados:', profs);
    
    // Inicializar serviços existentes
    const servs = existingServices.map(s => ({
      id: s.id,
      name: s.name,
      isNew: false,
    }));
    setAllServices(servs);
    console.log('[PlaceCreationWizard] ✅ Serviços inicializados:', servs);
  }, [existingProfessionals, existingServices]);

  // Profissionais selecionados
  const selectedProfessionals = useMemo(() => {
    return professionals.filter(p => p.selected);
  }, [professionals]);

  // Steps labels
  const getStepLabels = useMemo(() => {
    const labels = ['Nome', 'Profissionais'];
    
    // Adiciona step para cada profissional selecionado
    selectedProfessionals.forEach(p => {
      labels.push(`Serviços: ${p.name.split(' ')[0]}`);
    });
    
    labels.push('Resumo');
    labels.push('Criação');
    
    return labels;
  }, [selectedProfessionals]);

  // Total de steps
  const totalSteps = useMemo(() => {
    return 2 + selectedProfessionals.length + 2; // Nome + Profissionais + N serviços + Resumo + Criação
  }, [selectedProfessionals]);

  // Verifica se step atual é válido para avançar
  const canProceed = useMemo(() => {
    if (activeStep === 0) {
      return placeName.trim().length > 0;
    }
    if (activeStep === 1) {
      return selectedProfessionals.length > 0;
    }
    // Steps de serviços - sempre pode avançar (serviços são opcionais)
    if (activeStep >= 2 && activeStep < 2 + selectedProfessionals.length) {
      return true;
    }
    // Resumo - sempre pode criar
    if (activeStep === 2 + selectedProfessionals.length) {
      return true;
    }
    return false;
  }, [activeStep, placeName, selectedProfessionals]);

  // Toggle seleção de profissional
  const toggleProfessional = (index) => {
    console.log(`[PlaceCreationWizard] 👤 Toggle profissional índice ${index}:`, professionals[index]);
    setProfessionals(prev => prev.map((p, i) => 
      i === index ? { ...p, selected: !p.selected } : p
    ));
  };

  // Adicionar novo profissional
  const addNewProfessional = () => {
    const name = newProfessionalName.trim();
    if (!name) return;
    
    console.log(`[PlaceCreationWizard] ➕ Tentando adicionar novo profissional: "${name}"`);
    
    // Verifica se já existe com mesmo nome
    const exists = professionals.some(p => 
      p.name.toLowerCase() === name.toLowerCase()
    );
    
    if (!exists) {
      const newProf = {
        id: null,
        name,
        selected: true,
      };
      console.log('[PlaceCreationWizard] ✅ Novo profissional adicionado:', newProf);
      setProfessionals(prev => [...prev, newProf]);
    } else {
      console.log('[PlaceCreationWizard] ⚠️ Profissional já existe, ignorando');
    }
    
    setNewProfessionalName('');
  };

  // Toggle seleção de serviço
  const toggleService = (serviceKey) => {
    console.log(`[PlaceCreationWizard] 🔧 Toggle serviço: "${serviceKey}" para profissional índice ${currentProfessionalIndex}`);
    setProfessionalServices(prev => {
      const profIndex = currentProfessionalIndex;
      const current = prev[profIndex] || new Set();
      const newSet = new Set(current);
      
      if (newSet.has(serviceKey)) {
        newSet.delete(serviceKey);
        console.log(`[PlaceCreationWizard] ❌ Serviço "${serviceKey}" desmarcado`);
      } else {
        newSet.add(serviceKey);
        console.log(`[PlaceCreationWizard] ✅ Serviço "${serviceKey}" marcado`);
      }
      
      return { ...prev, [profIndex]: newSet };
    });
  };

  // Adicionar novo serviço
  const addNewService = () => {
    const name = newServiceName.trim();
    if (!name) return;
    
    console.log(`[PlaceCreationWizard] ➕ Tentando adicionar novo serviço: "${name}"`);
    
    // Verifica se já existe com mesmo nome
    const exists = allServices.some(s => 
      s.name.toLowerCase() === name.toLowerCase()
    );
    
    if (!exists) {
      const newService = {
        id: null,
        name,
        isNew: true,
      };
      console.log('[PlaceCreationWizard] ✅ Novo serviço adicionado:', newService);
      setAllServices(prev => [...prev, newService]);
      
      // Já seleciona o serviço para o profissional atual
      const serviceKey = `new_${name}`;
      console.log(`[PlaceCreationWizard] 🔗 Auto-selecionando serviço "${serviceKey}" para profissional índice ${currentProfessionalIndex}`);
      setProfessionalServices(prev => {
        const profIndex = currentProfessionalIndex;
        const current = prev[profIndex] || new Set();
        const newSet = new Set(current);
        newSet.add(serviceKey);
        return { ...prev, [profIndex]: newSet };
      });
    } else {
      console.log('[PlaceCreationWizard] ⚠️ Serviço já existe, ignorando');
    }
    
    setNewServiceName('');
  };

  // Chave única para serviço (id ou new_name)
  const getServiceKey = (service) => {
    return service.id || `new_${service.name}`;
  };

  // Verifica se serviço está selecionado para profissional atual
  const isServiceSelected = (service) => {
    const profIndex = currentProfessionalIndex;
    const selected = professionalServices[profIndex] || new Set();
    return selected.has(getServiceKey(service));
  };

  // Avançar step
  const handleNext = () => {
    console.log(`[PlaceCreationWizard] ➡️ Avançando do step ${activeStep} para ${activeStep + 1}`);
    
    if (activeStep === 1) {
      // Após selecionar profissionais, inicializa índice para primeiro
      console.log('[PlaceCreationWizard] 📋 Profissionais selecionados:', selectedProfessionals);
      setCurrentProfessionalIndex(0);
    } else if (activeStep >= 2 && activeStep < 2 + selectedProfessionals.length - 1) {
      // Avança para próximo profissional
      console.log(`[PlaceCreationWizard] 👤 Avançando para próximo profissional (índice ${currentProfessionalIndex + 1})`);
      setCurrentProfessionalIndex(prev => prev + 1);
    }
    
    if (activeStep === 0) {
      console.log(`[PlaceCreationWizard] 📝 Nome do local definido: "${placeName}"`);
    }
    
    setActiveStep(prev => prev + 1);
  };

  // Voltar step
  const handleBack = () => {
    console.log(`[PlaceCreationWizard] ⬅️ Voltando do step ${activeStep} para ${activeStep - 1}`);
    
    if (activeStep >= 3 && activeStep <= 2 + selectedProfessionals.length) {
      // Voltando nos steps de profissionais
      console.log(`[PlaceCreationWizard] 👤 Voltando para profissional anterior (índice ${currentProfessionalIndex - 1})`);
      setCurrentProfessionalIndex(prev => prev - 1);
    }
    setActiveStep(prev => prev - 1);
  };

  // ==================== CRIAÇÃO ====================

  const startCreation = async () => {
    console.log('[PlaceCreationWizard] 🏁 INICIANDO PROCESSO DE CRIAÇÃO');
    console.log('[PlaceCreationWizard] ===================================');
    
    setCreating(true);
    setCreationProgress(0);
    setCreationStatus([]);
    setCreationError(null);
    setActiveStep(totalSteps - 1); // Vai para step de criação

    try {
      // Coletar todos os serviços novos a criar
      const newServicesToCreate = allServices.filter(s => s.isNew);
      // Coletar profissionais novos a criar
      const newProfessionalsToCreate = selectedProfessionals.filter(p => !p.id);
      
      console.log('[PlaceCreationWizard] 📊 RESUMO DA CRIAÇÃO:');
      console.log(`[PlaceCreationWizard]   - Nome do local: "${placeName}"`);
      console.log('[PlaceCreationWizard]   - Serviços novos a criar:', newServicesToCreate);
      console.log('[PlaceCreationWizard]   - Profissionais novos a criar:', newProfessionalsToCreate);
      console.log('[PlaceCreationWizard]   - Profissionais selecionados (total):', selectedProfessionals);
      console.log('[PlaceCreationWizard]   - Mapa de serviços por profissional:', professionalServices);
      console.log('[PlaceCreationWizard]   - Todos os serviços disponíveis:', allServices);
      
      // Calcular total de operações
      const totalOperations = 
        newServicesToCreate.length + // Criar serviços
        newProfessionalsToCreate.length + // Criar profissionais
        1 + // Criar local
        selectedProfessionals.length + // Vincular profissionais ao local
        Object.keys(professionalServices).length; // Vincular serviços (aproximado)
      
      console.log(`[PlaceCreationWizard] 📈 Total de operações estimadas: ${totalOperations}`);
      
      let completedOps = 0;
      const updateProgress = () => {
        completedOps++;
        const progress = (completedOps / totalOperations) * 100;
        console.log(`[PlaceCreationWizard] 📊 Progresso: ${completedOps}/${totalOperations} (${progress.toFixed(1)}%)`);
        setCreationProgress(progress);
      };

      // Mapa para guardar IDs criados
      const serviceIdMap = {}; // name -> id
      const professionalIdMap = {}; // name -> id

      // 1. Criar serviços novos
      console.log('[PlaceCreationWizard] 📦 ETAPA 1: Criando serviços novos...');
      for (const service of newServicesToCreate) {
        console.log(`[PlaceCreationWizard] 🔧 Criando serviço: "${service.name}"`);
        setCreationStatus(prev => [...prev, { text: `Criando serviço "${service.name}"...`, done: false }]);
        
        const created = await servicesService.create({ name: service.name });
        console.log(`[PlaceCreationWizard] ✅ Serviço criado! ID: ${created.id}`, created);
        
        serviceIdMap[service.name] = created.id;
        setCreationStatus(prev => {
          const updated = [...prev];
          updated[updated.length - 1].done = true;
          return updated;
        });
        updateProgress();
        await delay(API_DELAY_MS);
      }
      console.log('[PlaceCreationWizard] 📦 Mapa de IDs de serviços criados:', serviceIdMap);

      // 2. Criar profissionais novos
      console.log('[PlaceCreationWizard] 👥 ETAPA 2: Criando profissionais novos...');
      for (const prof of newProfessionalsToCreate) {
        console.log(`[PlaceCreationWizard] 👤 Criando profissional: "${prof.name}"`);
        setCreationStatus(prev => [...prev, { text: `Criando profissional "${prof.name}"...`, done: false }]);
        
        const created = await professionalsService.create({ name: prof.name });
        console.log(`[PlaceCreationWizard] ✅ Profissional criado! ID: ${created.id}`, created);
        
        professionalIdMap[prof.name] = created.id;
        setCreationStatus(prev => {
          const updated = [...prev];
          updated[updated.length - 1].done = true;
          return updated;
        });
        updateProgress();
        await delay(API_DELAY_MS);
      }
      console.log('[PlaceCreationWizard] 👥 Mapa de IDs de profissionais criados:', professionalIdMap);

      // 3. Criar local
      console.log('[PlaceCreationWizard] 🏢 ETAPA 3: Criando local...');
      console.log(`[PlaceCreationWizard] 🏢 Nome do local: "${placeName}"`);
      setCreationStatus(prev => [...prev, { text: `Criando ${placeSingular} "${placeName}"...`, done: false }]);
      
      const createdPlace = await placesService.create({ name: placeName });
      console.log(`[PlaceCreationWizard] ✅ Local criado! ID: ${createdPlace.id}`, createdPlace);
      
      setCreationStatus(prev => {
        const updated = [...prev];
        updated[updated.length - 1].done = true;
        return updated;
      });
      updateProgress();
      await delay(API_DELAY_MS);

      // 4. Vincular profissionais ao local
      console.log('[PlaceCreationWizard] 🔗 ETAPA 4: Vinculando profissionais ao local...');
      const placeProfessionalIdMap = {}; // professionalId -> placeProfessionalId
      for (const prof of selectedProfessionals) {
        const professionalId = prof.id || professionalIdMap[prof.name];
        console.log(`[PlaceCreationWizard] 🔗 Vinculando profissional "${prof.name}" (ID: ${professionalId}) ao local (ID: ${createdPlace.id})`);
        
        setCreationStatus(prev => [...prev, { text: `Vinculando "${prof.name}" ao ${placeSingular}...`, done: false }]);
        const created = await placeProfessionalsService.create(createdPlace.id, { professionalId });
        console.log(`[PlaceCreationWizard] ✅ PlaceProfessional criado! ID: ${created.id}`, created);
        
        placeProfessionalIdMap[professionalId] = created.id;
        setCreationStatus(prev => {
          const updated = [...prev];
          updated[updated.length - 1].done = true;
          return updated;
        });
        updateProgress();
        await delay(API_DELAY_MS);
      }
      console.log('[PlaceCreationWizard] 🔗 Mapa de PlaceProfessional IDs:', placeProfessionalIdMap);

      // 5. Vincular serviços ao local e aos profissionais
      console.log('[PlaceCreationWizard] 🔧 ETAPA 5: Vinculando serviços ao local...');
      // Primeiro, descobrir todos os serviços únicos selecionados
      const allSelectedServiceKeys = new Set();
      Object.values(professionalServices).forEach(serviceSet => {
        serviceSet.forEach(key => allSelectedServiceKeys.add(key));
      });
      console.log('[PlaceCreationWizard] 🔧 Serviços únicos selecionados:', Array.from(allSelectedServiceKeys));

      // Criar PlaceService para cada serviço único
      const placeServiceIdMap = {}; // serviceId -> placeServiceId
      for (const serviceKey of allSelectedServiceKeys) {
        // Encontrar o serviço
        const service = allServices.find(s => getServiceKey(s) === serviceKey);
        if (!service) {
          console.log(`[PlaceCreationWizard] ⚠️ Serviço não encontrado para key: "${serviceKey}"`);
          continue;
        }
        
        const serviceId = service.id || serviceIdMap[service.name];
        if (!serviceId) {
          console.log(`[PlaceCreationWizard] ⚠️ ServiceId não encontrado para: "${service.name}"`);
          continue;
        }
        
        // Criar PlaceService se ainda não existe
        if (!placeServiceIdMap[serviceId]) {
          console.log(`[PlaceCreationWizard] 🔧 Criando PlaceService: serviceId=${serviceId} para local ${createdPlace.id}`);
          setCreationStatus(prev => [...prev, { text: `Adicionando serviço "${service.name}" ao ${placeSingular}...`, done: false }]);
          
          const created = await placeServicesService.create(createdPlace.id, { serviceId });
          console.log(`[PlaceCreationWizard] ✅ PlaceService criado!`, created);
          
          placeServiceIdMap[serviceId] = created.id || serviceId; // Alguns endpoints retornam diferente
          setCreationStatus(prev => {
            const updated = [...prev];
            updated[updated.length - 1].done = true;
            return updated;
          });
          await delay(API_DELAY_MS);
        }
      }
      console.log('[PlaceCreationWizard] 🔧 Mapa de PlaceService IDs:', placeServiceIdMap);

      // 6. Vincular profissionais aos serviços do local
      console.log('[PlaceCreationWizard] 🔗 ETAPA 6: Vinculando profissionais aos serviços...');
      for (let profIndex = 0; profIndex < selectedProfessionals.length; profIndex++) {
        const prof = selectedProfessionals[profIndex];
        const professionalId = prof.id || professionalIdMap[prof.name];
        const placeProfessionalId = placeProfessionalIdMap[professionalId];
        const serviceKeys = professionalServices[profIndex] || new Set();

        console.log(`[PlaceCreationWizard] 👤 Processando profissional "${prof.name}" (índice ${profIndex})`);
        console.log(`[PlaceCreationWizard]   - professionalId: ${professionalId}`);
        console.log(`[PlaceCreationWizard]   - placeProfessionalId: ${placeProfessionalId}`);
        console.log(`[PlaceCreationWizard]   - serviceKeys:`, Array.from(serviceKeys));

        for (const serviceKey of serviceKeys) {
          const service = allServices.find(s => getServiceKey(s) === serviceKey);
          if (!service) {
            console.log(`[PlaceCreationWizard] ⚠️ Serviço não encontrado para key: "${serviceKey}"`);
            continue;
          }
          
          const serviceId = service.id || serviceIdMap[service.name];
          if (!serviceId) {
            console.log(`[PlaceCreationWizard] ⚠️ ServiceId não encontrado para: "${service.name}"`);
            continue;
          }

          // Buscar o PlaceServiceId (ID do vínculo place-service, não do service)
          const placeServiceId = placeServiceIdMap[serviceId];
          if (!placeServiceId) {
            console.log(`[PlaceCreationWizard] ⚠️ PlaceServiceId não encontrado para serviceId: ${serviceId}`);
            continue;
          }

          try {
            console.log(`[PlaceCreationWizard] 🔗 Criando PlaceServiceProfessional:`);
            console.log(`[PlaceCreationWizard]   - placeId: ${createdPlace.id}`);
            console.log(`[PlaceCreationWizard]   - serviceId: ${serviceId}`);
            console.log(`[PlaceCreationWizard]   - placeServiceId (usado na rota): ${placeServiceId}`);
            console.log(`[PlaceCreationWizard]   - placeProfessionalId: ${placeProfessionalId}`);
            
            setCreationStatus(prev => [...prev, { text: `Vinculando "${prof.name}" ao serviço "${service.name}"...`, done: false }]);
            // IMPORTANTE: A rota usa placeServiceId, não serviceId!
            const result = await placeServiceProfessionalsService.create(createdPlace.id, placeServiceId, { placeProfessionalId });
            console.log(`[PlaceCreationWizard] ✅ PlaceServiceProfessional criado!`, result);
            
            setCreationStatus(prev => {
              const updated = [...prev];
              updated[updated.length - 1].done = true;
              return updated;
            });
            await delay(API_DELAY_MS);
          } catch (err) {
            // Pode falhar se já existe vínculo, ignora
            console.warn('[PlaceCreationWizard] ⚠️ Erro ao vincular profissional ao serviço:', err);
            console.warn('[PlaceCreationWizard] ⚠️ Detalhes:', err.response?.data);
            setCreationStatus(prev => {
              const updated = [...prev];
              updated[updated.length - 1].done = true;
              updated[updated.length - 1].warning = true;
              return updated;
            });
          }
          updateProgress();
        }
      }

      console.log('[PlaceCreationWizard] ✅ CRIAÇÃO CONCLUÍDA COM SUCESSO!');
      console.log('[PlaceCreationWizard] ===================================');
      console.log('[PlaceCreationWizard] 📊 RESUMO FINAL:');
      console.log(`[PlaceCreationWizard]   - Local criado: ${createdPlace.id}`);
      console.log('[PlaceCreationWizard]   - Serviços criados:', serviceIdMap);
      console.log('[PlaceCreationWizard]   - Profissionais criados:', professionalIdMap);
      console.log('[PlaceCreationWizard]   - PlaceProfessionals:', placeProfessionalIdMap);
      console.log('[PlaceCreationWizard]   - PlaceServices:', placeServiceIdMap);
      
      // Guardar dados para possível criação de agenda
      const professionalsWithIds = selectedProfessionals.map(p => ({
        id: p.id || professionalIdMap[p.name],
        name: p.name,
      }));
      setCreatedPlaceData({
        id: createdPlace.id,
        name: placeName,
        professionals: professionalsWithIds,
      });
      
      setCreationProgress(100);
      setCreationComplete(true);
      setCreationStatus(prev => [...prev, { text: '✅ Tudo pronto!', done: true, success: true }]);

    } catch (error) {
      console.error('[PlaceCreationWizard] ❌ ERRO NA CRIAÇÃO:', error);
      console.error('[PlaceCreationWizard] ❌ Response data:', error.response?.data);
      console.error('[PlaceCreationWizard] ❌ Stack:', error.stack);
      setCreationError(error.response?.data?.message || error.message || 'Erro ao criar');
    } finally {
      console.log('[PlaceCreationWizard] 🏁 Processo de criação finalizado');
      setCreating(false);
    }
  };

  // Finalizar e fechar
  const handleFinish = () => {
    console.log('[PlaceCreationWizard] 🎉 Finalizando wizard');
    console.log(`[PlaceCreationWizard]   - creationComplete: ${creationComplete}`);
    console.log(`[PlaceCreationWizard]   - onSuccess callback: ${!!onSuccess}`);
    
    onClose();
    if (creationComplete && onSuccess) {
      console.log('[PlaceCreationWizard] 📞 Chamando callback onSuccess');
      onSuccess();
    }
  };
  
  // Callback quando wizard de agenda fecha
  const handleScheduleWizardClose = () => {
    console.log('[PlaceCreationWizard] 📅 Wizard de agenda fechado');
    setShowScheduleWizard(false);
  };
  
  // Callback quando agendas são criadas com sucesso
  const handleScheduleWizardSuccess = (schedules) => {
    console.log('[PlaceCreationWizard] 📅 Agendas criadas:', schedules);
    setShowScheduleWizard(false);
    handleFinish();
  };

  // ==================== RENDER ====================

  const renderStepContent = () => {
    // Step 1: Nome do local
    if (activeStep === 0) {
      return (
        <Box sx={{ py: 2 }}>
          <Typography variant="h6" gutterBottom>
            Qual é o nome {placePreposition} {placeSingular} que você quer criar?
          </Typography>
          
          <TextField
            autoFocus
            fullWidth
            label={`Nome ${placePreposition} ${placeSingular}`}
            value={placeName}
            onChange={(e) => setPlaceName(e.target.value)}
            sx={{ mt: 2 }}
            placeholder={`Ex: Clínica Centro, Studio Downtown...`}
          />
          
          <Alert severity="info" sx={{ mt: 3 }}>
            Você pode alterar esses dados posteriormente editando {placeArticle} {placeSingular}.
          </Alert>
        </Box>
      );
    }

    // Step 2: Profissionais
    if (activeStep === 1) {
      return (
        <Box sx={{ py: 2 }}>
          <Typography variant="h6" gutterBottom>
            Quais profissionais trabalharão em <strong>{placeName}</strong>?
          </Typography>
          
          {professionals.length === 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Você ainda não tem profissionais cadastrados. Adicione um novo abaixo.
            </Alert>
          )}
          
          <List dense sx={{ maxHeight: 300, overflow: 'auto', mb: 2 }}>
            {professionals.map((prof, index) => (
              <ListItem 
                key={prof.id || `new_${index}`} 
                button 
                onClick={() => toggleProfessional(index)}
                sx={{ 
                  borderRadius: 1, 
                  mb: 0.5,
                  bgcolor: prof.selected ? 'action.selected' : 'transparent',
                }}
              >
                <ListItemIcon>
                  <Checkbox
                    edge="start"
                    checked={prof.selected}
                    tabIndex={-1}
                    disableRipple
                  />
                </ListItemIcon>
                <ListItemText 
                  primary={prof.name} 
                  secondary={!prof.id ? '(Novo)' : null}
                />
                {!prof.id && (
                  <Chip size="small" label="Novo" color="primary" variant="outlined" />
                )}
              </ListItem>
            ))}
          </List>
          
          <TextField
            fullWidth
            label="Adicionar novo profissional"
            placeholder="Digite o nome e pressione Enter"
            value={newProfessionalName}
            onChange={(e) => setNewProfessionalName(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addNewProfessional();
              }
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={addNewProfessional} edge="end">
                    <AddIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          
          <Alert severity="info" sx={{ mt: 3 }}>
            Você pode alterar os profissionais posteriormente editando {placeArticle} {placeSingular}.
          </Alert>
        </Box>
      );
    }

    // Steps 3+: Serviços por profissional
    const servicesStepStart = 2;
    const servicesStepEnd = 2 + selectedProfessionals.length;
    
    if (activeStep >= servicesStepStart && activeStep < servicesStepEnd) {
      const prof = selectedProfessionals[currentProfessionalIndex];
      
      return (
        <Box sx={{ py: 2 }}>
          <Typography variant="h6" gutterBottom>
            Quais serviços <strong>{prof.name}</strong> vai executar em <strong>{placeName}</strong>?
          </Typography>
          
          {allServices.length === 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Você ainda não tem serviços cadastrados. Adicione um novo abaixo.
            </Alert>
          )}
          
          <List dense sx={{ maxHeight: 300, overflow: 'auto', mb: 2 }}>
            {allServices.map((service) => {
              const serviceKey = getServiceKey(service);
              const isSelected = isServiceSelected(service);
              
              return (
                <ListItem 
                  key={serviceKey} 
                  button 
                  onClick={() => toggleService(serviceKey)}
                  sx={{ 
                    borderRadius: 1, 
                    mb: 0.5,
                    bgcolor: isSelected ? 'action.selected' : 'transparent',
                  }}
                >
                  <ListItemIcon>
                    <Checkbox
                      edge="start"
                      checked={isSelected}
                      tabIndex={-1}
                      disableRipple
                    />
                  </ListItemIcon>
                  <ListItemText 
                    primary={service.name} 
                    secondary={service.isNew ? '(Novo)' : null}
                  />
                  {service.isNew && (
                    <Chip size="small" label="Novo" color="secondary" variant="outlined" />
                  )}
                </ListItem>
              );
            })}
          </List>
          
          <TextField
            fullWidth
            label="Adicionar novo serviço"
            placeholder="Digite o nome e pressione Enter"
            value={newServiceName}
            onChange={(e) => setNewServiceName(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addNewService();
              }
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={addNewService} edge="end">
                    <AddIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          
          <Alert severity="info" sx={{ mt: 3 }}>
            Serviços novos criados aqui ficam disponíveis para os próximos profissionais. 
            Você pode ajustar posteriormente.
          </Alert>
        </Box>
      );
    }

    // Step Resumo
    if (activeStep === servicesStepEnd) {
      return (
        <Box sx={{ py: 2 }}>
          <Typography variant="h6" gutterBottom>
            📋 Resumo da Criação
          </Typography>
          
          <Box sx={{ p: 2, bgcolor: 'background.paper', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
            <Typography paragraph>
              {placeArticle.charAt(0).toUpperCase() + placeArticle.slice(1)} {placeSingular} <strong>{placeName}</strong> será criad{placeArticle === 'a' ? 'a' : 'o'} com os seguintes profissionais e serviços:
            </Typography>
            
            <Divider sx={{ my: 2 }} />
            
            {selectedProfessionals.map((prof, index) => {
              const serviceKeys = professionalServices[index] || new Set();
              const services = Array.from(serviceKeys).map(key => {
                const service = allServices.find(s => getServiceKey(s) === key);
                return service?.name || key;
              });
              
              return (
                <Box key={prof.id || prof.name} sx={{ mb: 2 }}>
                  <Typography variant="subtitle1">
                    <strong>{prof.name}</strong> {!prof.id && <Chip size="small" label="Novo" color="primary" sx={{ ml: 1 }} />}
                  </Typography>
                  {services.length > 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                      Serviços: {services.map((s, i) => (
                        <span key={i}>
                          <strong>{s}</strong>
                          {i < services.length - 1 ? ', ' : ''}
                        </span>
                      ))}
                    </Typography>
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ ml: 2, fontStyle: 'italic' }}>
                      Nenhum serviço selecionado
                    </Typography>
                  )}
                </Box>
              );
            })}
          </Box>
          
          <Alert severity="info" sx={{ mt: 3 }}>
            Após a criação, você pode editar {placeArticle} {placeSingular}, profissionais e serviços a qualquer momento.
          </Alert>
        </Box>
      );
    }

    // Step Criação (progresso)
    if (activeStep === servicesStepEnd + 1) {
      return (
        <Box sx={{ py: 2 }}>
          <Typography variant="h6" gutterBottom>
            {creationComplete ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CelebrationIcon color="success" />
                {placeSingular.charAt(0).toUpperCase() + placeSingular.slice(1)} criad{placeArticle === 'a' ? 'a' : 'o'} com sucesso!
              </Box>
            ) : creationError ? (
              'Erro na criação'
            ) : (
              'Criando...'
            )}
          </Typography>
          
          {!creationError && (
            <Box sx={{ mb: 3 }}>
              <LinearProgress 
                variant="determinate" 
                value={creationProgress} 
                sx={{ height: 10, borderRadius: 5 }}
                color={creationComplete ? 'success' : 'primary'}
              />
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block', textAlign: 'right' }}>
                {Math.round(creationProgress)}%
              </Typography>
            </Box>
          )}
          
          {creationError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {creationError}
            </Alert>
          )}
          
          <List dense sx={{ maxHeight: 300, overflow: 'auto' }}>
            {creationStatus.map((status, index) => (
              <ListItem key={index}>
                <ListItemIcon>
                  {status.done ? (
                    <CheckIcon color={status.success ? 'success' : status.warning ? 'warning' : 'action'} />
                  ) : (
                    <CircularProgress size={20} />
                  )}
                </ListItemIcon>
                <ListItemText 
                  primary={status.text}
                  primaryTypographyProps={{
                    color: status.success ? 'success.main' : 'text.primary',
                    fontWeight: status.success ? 'bold' : 'normal',
                  }}
                />
              </ListItem>
            ))}
          </List>
          
          {/* Pergunta sobre criar agenda após sucesso */}
          {creationComplete && createdPlaceData && (
            <Alert 
              severity="info" 
              icon={<CalendarIcon />}
              sx={{ mt: 3 }}
              action={
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button 
                    color="inherit" 
                    size="small"
                    onClick={() => setShowScheduleWizard(true)}
                  >
                    Sim, criar agendas
                  </Button>
                  <Button 
                    color="inherit" 
                    size="small" 
                    variant="outlined"
                    onClick={handleFinish}
                  >
                    Não, depois
                  </Button>
                </Box>
              }
            >
              <Typography variant="body2">
                Deseja aproveitar para criar agendas de atendimento para os profissionais em <strong>{createdPlaceData.name}</strong>?
              </Typography>
            </Alert>
          )}
        </Box>
      );
    }

    return null;
  };

  return (
    <Dialog 
      open={open} 
      onClose={creating ? undefined : onClose}
      maxWidth="md" 
      fullWidth
      TransitionProps={{
        onEnter: handleDialogEnter,
      }}
    >
      <DialogTitle>
        Criar {placeArticle === 'a' ? 'nova' : 'novo'} {placeSingular}
      </DialogTitle>
      
      <DialogContent dividers>
        {/* Stepper - só mostra se não estiver no step de criação */}
        {activeStep < totalSteps - 1 && (
          <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 3 }}>
            {getStepLabels.slice(0, -1).map((label, index) => (
              <Step key={index}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        )}
        
        {renderStepContent()}
      </DialogContent>
      
      <DialogActions sx={{ px: 3, py: 2 }}>
        {/* Botão Voltar */}
        {activeStep > 0 && activeStep < totalSteps - 1 && !creating && (
          <Button 
            onClick={handleBack}
            startIcon={<ArrowBackIcon />}
          >
            Voltar
          </Button>
        )}
        
        <Box sx={{ flex: 1 }} />
        
        {/* Botão Cancelar */}
        {!creating && !creationComplete && (
          <Button onClick={onClose}>
            Cancelar
          </Button>
        )}
        
        {/* Botão Avançar/Confirmar/Criar */}
        {activeStep < 2 + selectedProfessionals.length && (
          <Button
            variant="contained"
            onClick={handleNext}
            disabled={!canProceed}
            endIcon={<ArrowForwardIcon />}
          >
            {activeStep === 1 ? 'Confirmar' : 'Continuar'}
          </Button>
        )}
        
        {/* Botão Próximo (step de serviços) */}
        {activeStep >= 2 && activeStep < 2 + selectedProfessionals.length - 1 && (
          <Button
            variant="contained"
            onClick={handleNext}
            endIcon={<ArrowForwardIcon />}
          >
            Próximo Profissional
          </Button>
        )}
        
        {/* Botão Ir para Resumo (último profissional) */}
        {activeStep === 2 + selectedProfessionals.length - 1 && selectedProfessionals.length > 0 && (
          <Button
            variant="contained"
            onClick={handleNext}
            endIcon={<ArrowForwardIcon />}
          >
            Ver Resumo
          </Button>
        )}
        
        {/* Botão Criar (step resumo) */}
        {activeStep === 2 + selectedProfessionals.length && (
          <Button
            variant="contained"
            color="success"
            onClick={startCreation}
            disabled={creating}
            startIcon={creating ? <CircularProgress size={20} /> : <CheckIcon />}
          >
            Criar {placeSingular.charAt(0).toUpperCase() + placeSingular.slice(1)}
          </Button>
        )}
        
        {/* Botão Fechar (step criação completa) - só mostra se não tiver pergunta de agenda ativa */}
        {creationComplete && !createdPlaceData && (
          <Button
            variant="contained"
            color="success"
            onClick={handleFinish}
          >
            Concluir
          </Button>
        )}
        
        {/* Botão Fechar (erro) */}
        {creationError && (
          <Button
            variant="outlined"
            onClick={handleFinish}
          >
            Fechar
          </Button>
        )}
      </DialogActions>
      
      {/* Wizard de criação de agenda */}
      {showScheduleWizard && createdPlaceData && (
        <ScheduleCreationWizard
          open={showScheduleWizard}
          onClose={handleScheduleWizardClose}
          onSuccess={handleScheduleWizardSuccess}
          placeName={createdPlaceData.name}
          placeId={createdPlaceData.id}
          professionals={createdPlaceData.professionals}
        />
      )}
    </Dialog>
  );
}
